<template>
  <asc-page-loading-indicator :loading="isSubscriptionCenterLoading">
    <v-container v-if="!isSubscriptionCenterPageActive" fluid>
      <asc-page-coming-soon
        label="Subscription Center Page Coming Soon!"
      ></asc-page-coming-soon>
    </v-container>

    <v-container v-else fluid class="asc-subscription-center-container">
      <v-row no-gutters>
        <v-col cols="12" sm="12">
          <asc-page-header>
            <template #pageTitle>
              <asc-page-title title="SUBSCRIPTION CENTER" />
            </template>
          </asc-page-header>
        </v-col>
      </v-row>
      <asc-container>
        <template slot="subheader">
          <asc-surface-support-text
            text="Activate and manage subscriptions you receive"
          ></asc-surface-support-text>
        </template>
        <asc-subscription-center-template
          gid-title="My Subscriptions"
          :values="getAvailableSubscriptions"
          @onSubscriptionActiveToggle="handleSubscriptionActiveToggle"
          @onSubscriptionEdit="handleSubscriptionEdit"
        >
          <template #description>
            Activate and manage subscriptions you receive.
          </template>
        </asc-subscription-center-template>
      </asc-container>
    </v-container>
  </asc-page-loading-indicator>
</template>

<script>
import AscPageLoadingIndicator from '@/components/molecules/AscPageLoadingIndicator'
import { AscSubscriptionCenterTemplate } from '@/components/templates'
import AscPageHeader from '@/components/atoms/AscPageHeader'
import AscPageTitle from '@/components/atoms/AscPageTitle'
import AscSurfaceSupportText from '@/components/atoms/AscSurfaceSupportText'
import AscContainer from '@/components/atoms/AscContainer'

import AscPageComingSoon from '@/components/molecules/AscPageComingSoon'
import AppFeatureFlags from '@/utilities/appFeatureFlags'

import { mapState, mapActions, mapGetters } from 'vuex'
import {
  SUBSCRIPTION_CENTER_CONSTS as SCC,
  USER_CONSTS as UC,
} from '@/stores/consts'

import _ from 'lodash'

export default {
  name: 'AscSubscriptionCenterContainer',

  components: {
    AscPageComingSoon,
    AscPageHeader,
    AscPageTitle,
    AscContainer,
    AscSurfaceSupportText,
    AscPageLoadingIndicator,
    AscSubscriptionCenterTemplate,
  },

  data() {
    return {}
  },

  computed: {
    ...mapState(SCC.NAMESPACE, [
      'subscriptionsTypes',
      'subscriptions',
      'subscriptionsLoading',
    ]),
    ...mapGetters(UC.NAMESPACE, [
      'subscriptionAcmfAnalyticsPermission',
      'subscriptionAcmfReportsPermission',
      'subscriptionMaintenancePermission',
    ]),

    isSubscriptionCenterLoading() {
      return this.subscriptionsLoading
    },

    isSubscriptionCenterPageActive() {
      return AppFeatureFlags.getSubscriptionCenter()
    },

    mappedSubscriptionGroups() {
      if (this.subscriptionsTypes && this.subscriptionsTypes.length) {
        const group = this.subscriptionsTypes.map(subscriptionGroup => {
          const groupByName = _.groupBy(this.subscriptionsTypes, 'groupName')[
            subscriptionGroup.groupName
          ]

          return {
            ...subscriptionGroup,
            ...{ name: groupByName[0].groupDisplayName },
            ...{
              emailTypes: groupByName.map(
                ({ filterable, id, name, subscribable }) => {
                  return {
                    filterable,
                    id,
                    name,
                    subscribable,
                  }
                },
              ),
            },
          }
        })
        return group
      }
      return []
    },

    currentSubscriptionsByGroup() {
      const uniqueSubscriptions = _.uniqBy(
        this.mappedSubscriptionGroups,
        subscription => {
          return subscription.groupName
        },
      )

      return uniqueSubscriptions.map(group => {
        const isActive = []
        const frequency = []
        const isValid = [] // not valid if no filters selected
        const isAnyNotificationEnabled = []

        const subscribableEmailTypes = group.emailTypes.filter(emailTypeObj => {
          return emailTypeObj.subscribable
        })

        const emailTypes = subscribableEmailTypes.map(emailType => {
          const curentSubscription = this.subscriptions.find(
            subscription => subscription.emailTypeId === emailType.id,
          )

          if (curentSubscription) {
            isActive.push(curentSubscription.isActive)
            frequency.push(curentSubscription.frequency)
            isValid.push(
              curentSubscription.filter && curentSubscription.filter.length > 0,
            )
            isAnyNotificationEnabled.push(curentSubscription.isEnabled)
            return curentSubscription
          } else {
            // Defaults if not exists
            isActive.push(false)
            frequency.push(1) // Default frequency which is immediate
            isValid.push(false)
            isAnyNotificationEnabled.push(false)
            return {
              emailType: emailType.name,
              emailTypeId: emailType.id,
              filter: [],
              frequency: 1, // default to immediate
              isActive: false,
              isEnabled: false,
            }
          }
        })

        const isDisabled =
          emailTypes.length > 1
            ? isValid.every(item => item === false) ||
              isAnyNotificationEnabled.every(item => item === false)
            : isValid.includes(false)

        return {
          ...group,
          ...{ emailTypes },
          ...{ isActive: isActive.includes(true) },
          ...{ frequency: frequency },
          ...{
            disabled: isDisabled,
          },
        }
      })
    },

    getAvailableSubscriptions() {
      let updatedAcmfAnalyticsSubscription = []
      let updatedAcmfReportSubscription = []
      let updatedMaintenanceSubscription = []
      const uniqueSubscriptions = _.uniqBy(
        this.currentSubscriptionsByGroup,
        subscription => {
          return subscription.groupName
        },
      )
      if (uniqueSubscriptions.length > 0) {
        if (this.subscriptionAcmfAnalyticsPermission) {
          updatedAcmfAnalyticsSubscription = uniqueSubscriptions.filter(
            item => {
              return item.groupName === 'ACMF_ANALYTIC_ALERT'
            },
          )
        }
        if (this.subscriptionAcmfReportsPermission) {
          updatedAcmfReportSubscription = uniqueSubscriptions.filter(item => {
            return item.groupName === 'ACMF_REPORT'
          })
        }
        if (this.subscriptionMaintenancePermission) {
          updatedMaintenanceSubscription = uniqueSubscriptions.filter(item => {
            return item.groupName === 'MX_REC'
          })
        }
      }
      return updatedAcmfAnalyticsSubscription
        .concat(updatedAcmfReportSubscription)
        .concat(updatedMaintenanceSubscription)
    },
  },

  async created() {
    // the email subscription type endpoint only returns types
    // that the user has access to.  So no need to check for roles
    await this[SCC.GET_SUBSCRIPTIONS_TYPES]()
    const emailSubscriptionIds = this.subscriptionsTypes.map(subscription => {
      return subscription.id
    })
    this.getSubscriptionCenterData(emailSubscriptionIds)
  },

  methods: {
    ...mapActions(SCC.NAMESPACE, [
      SCC.GET_SUBSCRIPTIONS_TYPES,
      SCC.GET_SUBSCRIPTIONS,
      SCC.SAVE_SUBSCRIPTIONS,
      SCC.UPDATE_SUBSCRIPTION,
    ]),

    async getSubscriptionCenterData(associatedEmailTypeIds) {
      await this[SCC.GET_SUBSCRIPTIONS](associatedEmailTypeIds)
    },

    async handleSubscriptionActiveToggle(subscription) {
      await this[SCC.UPDATE_SUBSCRIPTION]({
        groupName: subscription.name.split(' ').join('-').toLowerCase(),
        subscriptionsToSave: subscription.emailTypes.map(s => {
          return { ...s, ...{ isActive: subscription.isActive } }
        }),
        subscriptionIds: subscription.emailTypes.map(s => s.emailTypeId),
        isNew: false,
      })
    },

    handleSubscriptionEdit({ emailTypes, name }) {
      if (emailTypes.length && name) {
        const routeObj = {
          name: 'Subscription Details',
          params: {
            ...{
              groupName: name.split(' ').join('-').toLowerCase(),
              displayName: name,
            },
          },
        }

        this.$router.push(routeObj).catch(err => {
          throw new Error(`Problem navigating: ${err}.`)
        })
      }
    },
  },
}
</script>

<style lang="scss">
.asc-subscription-center-container {
  background-color: $gray-100;

  &--title {
    color: $primary-black;
    font-size: 14px;
    letter-spacing: 0;
    line-height: 18px;
    margin: 9px 0 21px 0;
    text-transform: uppercase;
    font-weight: normal;
  }
}
</style>
